/**
 * @file FileUploader component for uploading and managing files.
 * @description This component allows users to upload files, displays the selected file name, and provides options to remove the file. It also supports initial files and locking the input.
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import style from './ThemePage.module.scss';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { useContext } from 'react';
import { uploadFile } from '../../services/themeService';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { sanitizeFileName } from '../../utils/utils';
import Toast from '../ui/toasts/Toast';
import { getText } from '../../i18n';

/**
 * FileUploader component
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the file input
 * @param {Function} props.onFileChange - Callback function to handle file changes
 * @param {Object|string} [props.initialFile] - Initial file object or URL
 * @param {boolean} [props.locked] - Flag to lock the file input
 * @param {string} [props.accept="image/*"] - Accepted file types
 * @param {string} [props.inputText=""] - Additional text to display below the file input
 * @param {Function} [props.customValidation] - Custom validation function that returns a promise
 * @returns {JSX.Element} FileUploader component
 */
const FileUploader = ({
    id,
    onFileChange,
    initialFile,
    locked,
    accept = 'image/*',
    inputText = '',
    customValidation,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const { showModal } = useContext(ModalContext);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (typeof initialFile === 'string' && initialFile.startsWith('http')) {
            setSelectedFile(initialFile);
        } else {
            setSelectedFile(initialFile);
        }
    }, [initialFile]);

    const fileInputName = selectedFile
        ? typeof selectedFile === 'string'
            ? selectedFile.split('/').pop()
            : selectedFile.name
        : 'No image added';

    const onRemoveImage = () => {
        showModal({
            type: ModalTypes.ACTION,
            title: '',
            subtitle: getText('msg_427c3af3015d'),
            text: '',
            onYes: () => {
                setSelectedFile(null);
                onFileChange(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            },
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
    };

    const handleFileChange = async event => {
        const file = event.target.files[0];
        if (!file) return;

        const sanitizedFileName = sanitizeFileName(file.name);
        const sanitizedFile = new File([file], sanitizedFileName, {
            type: file.type,
            lastModified: file.lastModified,
        });

        if (customValidation) {
            const customValidationResult =
                await customValidation(sanitizedFile);
            if (customValidationResult)
                return Toast.error(customValidationResult);
        } else {
            const fileTypeAndSizeValidation =
                isValidFileTypeAndSize(sanitizedFile);
            if (fileTypeAndSizeValidation)
                return Toast.error(fileTypeAndSizeValidation);
        }

        try {
            const uploadedFile = await uploadFile(sanitizedFile);
            setSelectedFile(uploadedFile);
            onFileChange(uploadedFile);
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    return (
        <div
            className={`d-flex align-items-center column-gap-3 ${style.fileUploader} w-100`}
        >
            <input
                ref={fileInputRef}
                type="file"
                id={id}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={locked}
                accept={accept}
            />
            <button
                type="button"
                onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                }
                className="ing-button-primary-left align-self-baseline"
            >
                <i className="bi bi-upload"></i>
                {getText('msg_8bdf057f91e7')}
            </button>
            <div className={`form-group position-relative w-100`}>
                <input
                    onKeyDown={() => {}}
                    readOnly
                    type="text"
                    value={fileInputName}
                    className={`form-control`}
                    onChange={() => {}}
                    disabled={locked}
                />
                {inputText && (
                    <small className="form-text text-muted">{inputText}</small>
                )}
                <button
                    className={`btn btn-link ${style.deleteButton}`}
                    onClick={onRemoveImage}
                    disabled={locked || !selectedFile}
                    type="button"
                >
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>
        </div>
    );
};

FileUploader.propTypes = {
    onFileChange: PropTypes.func.isRequired,
    initialFile: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    locked: PropTypes.bool,
    accept: PropTypes.string,
    id: PropTypes.string.isRequired,
    customValidation: PropTypes.func,
};

export default FileUploader;
